"use client";

import * as React from "react";
import { motion } from "framer-motion";
import { Layers, Shapes, Map, Tag, PanelTop, Settings } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const capabilities = [{
  title: "Two-color acrylic engraving",
  description: "Cast two-color laminate (cap layer over contrasting core) engraved to reveal the base color. The standard material for industrial nameplates, pushbutton legend plates, and panel identification where phenolic is not specified.",
  icon: Layers
}, {
  title: "Laser-cut acrylic shapes",
  description: 'Custom shapes cut from DXF or AI files. Clean flame-polished edges on most thicknesses up to 1/4". Used for custom signs, decorative plaques, architectural letters, and non-standard nameplate formats.',
  icon: Shapes
}, {
  title: "Acrylic facility signs",
  description: "Room identification, door signs, directional signs, and wayfinding panels for offices, manufacturing facilities, healthcare, and education. Available with engraved or UV-printed text.",
  icon: Map
}, {
  title: "Industrial acrylic nameplates",
  description: "Equipment identification, machinery labels, and control panel nameplates in acrylic where phenolic is not required or where specific colors are needed outside standard phenolic color options.",
  icon: Tag
}, {
  title: "Acrylic standoff signs",
  description: "Precision-cut panels pre-drilled for standoff hardware. Used for reception signage, lobby displays, and branded interior signage in commercial and industrial facilities.",
  icon: PanelTop
}, {
  title: "Custom cut parts",
  description: "Acrylic components for OEM assemblies, display fixtures, enclosure windows, and protective covers. Cut to print from customer-supplied CAD files.",
  icon: Settings
}];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut"
    }
  }
};
export default function ServiceCapabilities() {
  return <section id="servicecapabilities" className="bg-background py-16 md:py-24 lg:py-32 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          
          {}
          <div className="lg:col-span-7 flex flex-col gap-10">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            amount: 0.2
          }} transition={{
            duration: 0.6,
            ease: "easeOut"
          }}>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-default font-bold text-foreground mb-6 tracking-tight">
                What Evermark Industrial Produces in Acrylic
              </h2>
              <p className="text-lg text-muted-foreground font-sans leading-relaxed max-w-2xl">
                Evermark Industrial's laser cutting and engraving systems handle
                both precision cutting and surface engraving in a single setup —
                no secondary operations required. Acrylic parts are cut
                clean-edged, engraved to consistent depth, and ready to install.
              </p>
            </motion.div>

            <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
            once: true,
            amount: 0.1
          }} className="flex flex-col gap-8">
              {capabilities.map((cap, i) => <motion.div key={i} data-index={i} variants={itemVariants} className="flex gap-5 group">
                  <div className="shrink-0 mt-1">
                    <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-secondary border border-border text-primary shadow-sm group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-300">
                      <cap.icon className="w-6 h-6" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-default font-semibold text-foreground mb-2">
                      {cap.title}
                    </h3>
                    <p className="text-muted-foreground font-sans leading-relaxed">
                      {cap.description}
                    </p>
                  </div>
                </motion.div>)}
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0.4
          }} className="pt-4">
              <Button asChild size="lg" className="font-sans font-semibold">
                <Link to="/ContactPage">Request a Quote</Link>
              </Button>
            </motion.div>
          </div>

          {}
          <div className="lg:col-span-5 lg:sticky lg:top-32">
            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true,
            amount: 0.2
          }} transition={{
            duration: 0.8,
            ease: "easeOut"
          }} className="relative rounded-2xl overflow-hidden border border-border shadow-2xl bg-card">
              <div className="absolute inset-0 bg-gradient-to-tr from-secondary/40 to-transparent z-10 mix-blend-overlay pointer-events-none" />
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/co2_laser_cutting_acrylic.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/co2_laser_cutting_acrylic.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/co2_laser_cutting_acrylic.webp-wvc-sizes" alt="CO₂ laser cutting cast acrylic sheet at Evermark Industrial Solutions facility" loading="lazy" className="w-full h-auto object-cover aspect-[4/5] md:aspect-square lg:aspect-[3/4]" />
            </motion.div>
          </div>

        </div>
      </div>
    </section>;
}